/**
 * P&ID 流程图几何干涉检查器
 *
 * 在浏览器里读取 React Flow 真实渲染结果，精确计算：
 *  1. 管线线段是否穿过设备标签文字（edge-through-label）
 *  2. 管线线段是否穿过其他设备图形本体（edge-through-device）
 *  3. 设备标签之间是否互相重叠（label-overlap）
 *  4. 管线中点标注是否压住设备标签 / 设备本体（edgelabel-through）
 *
 * 用法：打开任一详细图后，在控制台执行 `__pidLint()`，
 * 返回违规数组；空数组 = 通过。改图后必须跑到 0 违规才算完成。
 */

export interface PidViolation {
  type: 'edge-through-label' | 'edge-through-device' | 'label-overlap' | 'edgelabel-through';
  detail: string;
}

interface Seg {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

function parseViewportTransform(vp: Element | null): { tx: number; ty: number; s: number } {
  const t = vp instanceof HTMLElement ? vp.style.transform : '';
  const m = /translate\(([-\d.]+)px,\s*([-\d.]+)px\)\s*scale\(([\d.]+)\)/.exec(t);
  if (!m) return { tx: 0, ty: 0, s: 1 };
  return { tx: parseFloat(m[1]), ty: parseFloat(m[2]), s: parseFloat(m[3]) };
}

/** 解析 smoothstep 生成的 M/L/Q/C 路径为线段序列（局部坐标） */
function parsePathSegments(d: string): Seg[] {
  const segs: Seg[] = [];
  const re = /([MLQCAZ])([^MLQCAZ]*)/g;
  let cur: { x: number; y: number } | null = null;
  let m: RegExpExecArray | null;
  while ((m = re.exec(d))) {
    const cmd = m[1];
    const nums = (m[2].match(/-?[\d.]+/g) ?? []).map(Number);
    if (cmd === 'M') {
      if (nums.length >= 2) cur = { x: nums[0], y: nums[1] };
    } else if (cmd === 'L') {
      for (let i = 0; i + 1 < nums.length && cur; i += 2) {
        const p = { x: nums[i], y: nums[i + 1] };
        segs.push({ x1: cur.x, y1: cur.y, x2: p.x, y2: p.y });
        cur = p;
      }
    } else if (cmd === 'Q') {
      for (let i = 0; i + 3 < nums.length && cur; i += 4) {
        const c = { x: nums[i], y: nums[i + 1] };
        const p = { x: nums[i + 2], y: nums[i + 3] };
        segs.push({ x1: cur.x, y1: cur.y, x2: c.x, y2: c.y });
        segs.push({ x1: c.x, y1: c.y, x2: p.x, y2: p.y });
        cur = p;
      }
    } else if (cmd === 'A') {
      // 桥接半圆弧：以端点弦近似（不影响包围盒级别的相交判断）
      for (let i = 0; i + 6 < nums.length && cur; i += 7) {
        const p = { x: nums[i + 5], y: nums[i + 6] };
        segs.push({ x1: cur.x, y1: cur.y, x2: p.x, y2: p.y });
        cur = p;
      }
    } else if (cmd === 'C') {
      for (let i = 0; i + 5 < nums.length && cur; i += 6) {
        const c1 = { x: nums[i], y: nums[i + 1] };
        const c2 = { x: nums[i + 2], y: nums[i + 3] };
        const p = { x: nums[i + 4], y: nums[i + 5] };
        segs.push({ x1: cur.x, y1: cur.y, x2: c1.x, y2: c1.y });
        segs.push({ x1: c1.x, y1: c1.y, x2: c2.x, y2: c2.y });
        segs.push({ x1: c2.x, y1: c2.y, x2: p.x, y2: p.y });
        cur = p;
      }
    } else if (cmd === 'Z') {
      cur = null;
    }
  }
  return segs;
}

function segBox(seg: Seg): Box {
  return {
    left: Math.min(seg.x1, seg.x2),
    right: Math.max(seg.x1, seg.x2),
    top: Math.min(seg.y1, seg.y2),
    bottom: Math.max(seg.y1, seg.y2),
  };
}

function boxesOverlap(a: Box, b: Box, pad = 0): boolean {
  return !(
    a.right < b.left - pad ||
    a.left > b.right + pad ||
    a.bottom < b.top - pad ||
    a.top > b.bottom + pad
  );
}

export function collectPidViolations(): PidViolation[] {
  const pane = document.querySelector('.react-flow');
  const paneRect = pane ? pane.getBoundingClientRect() : { left: 0, top: 0 };
  const vp = document.querySelector('.react-flow__viewport');
  const { tx, ty, s } = parseViewportTransform(vp);
  const toScreen = (x: number, y: number) => ({
    x: paneRect.left + tx + s * x,
    y: paneRect.top + ty + s * y,
  });

  const violations: PidViolation[] = [];

  // ── 设备标签与本体 ──
  const labels: { text: string; box: Box }[] = [];
  const bodies: { text: string; box: Box }[] = [];
  document.querySelectorAll('.react-flow__node').forEach((nodeEl) => {
    const labelEl = nodeEl.querySelector('.pid-label');
    if (labelEl) {
      const r = labelEl.getBoundingClientRect();
      labels.push({
        text: (labelEl.textContent ?? '').trim().slice(0, 24),
        box: { left: r.left, top: r.top, right: r.right, bottom: r.bottom },
      });
    }
    const svg = nodeEl.querySelector('svg');
    if (svg) {
      const r = svg.getBoundingClientRect();
      bodies.push({
        text: (nodeEl.querySelector('.pid-label')?.textContent ?? nodeEl.id ?? '').trim().slice(0, 24),
        box: { left: r.left, top: r.top, right: r.right, bottom: r.bottom },
      });
    }
  });

  // ── 管线线段（屏幕坐标）──
  const edgeSegs: { id: string; segs: Seg[] }[] = [];
  document.querySelectorAll('.react-flow__edge').forEach((edgeEl, idx) => {
    const path = edgeEl.querySelector('.react-flow__edge-path');
    if (!path) return;
    const d = path.getAttribute('d') ?? '';
    if (!d) return;
    const id = edgeEl.getAttribute('data-id') ?? `edge#${idx}`;
    try {
      edgeSegs.push({
        id,
        segs: parsePathSegments(d).map((seg) => {
          const a = toScreen(seg.x1, seg.y1);
          const b = toScreen(seg.x2, seg.y2);
          return { x1: a.x, y1: a.y, x2: b.x, y2: b.y };
        }),
      });
    } catch {
      /* 跳过无法解析的路径 */
    }
  });

  // ── 管线中点标注 ──
  const edgeLabels: { text: string; box: Box; edgeId: string }[] = [];
  document.querySelectorAll('.react-flow__edge').forEach((edgeEl) => {
    const lbl = edgeEl.querySelector('.react-flow__edge-label');
    if (!lbl) return;
    const r = lbl.getBoundingClientRect();
    edgeLabels.push({
      text: (lbl.textContent ?? '').trim(),
      edgeId: edgeEl.getAttribute('data-id') ?? '?',
      box: { left: r.left, top: r.top, right: r.right, bottom: r.bottom },
    });
  });

  // 1) 管线穿设备标签
  for (const e of edgeSegs) {
    for (const seg of e.segs) {
      const b = segBox(seg);
      for (const l of labels) {
        if (boxesOverlap(b, l.box, 2)) {
          violations.push({
            type: 'edge-through-label',
            detail: `管线 ${e.id} 穿过标签「${l.text}」`,
          });
        }
      }
    }
  }

  // 2) 管线穿其他设备本体（本体收缩 3px，容忍管线在管口处的正常接触）
  for (const e of edgeSegs) {
    for (const seg of e.segs) {
      const b = segBox(seg);
      for (const body of bodies) {
        const shrunk: Box = {
          left: body.box.left + 3,
          top: body.box.top + 3,
          right: body.box.right - 3,
          bottom: body.box.bottom - 3,
        };
        if (boxesOverlap(b, shrunk)) {
          violations.push({
            type: 'edge-through-device',
            detail: `管线 ${e.id} 穿过设备本体「${body.text}」`,
          });
        }
      }
    }
  }

  // 3) 设备标签互相重叠
  for (let i = 0; i < labels.length; i++) {
    for (let j = i + 1; j < labels.length; j++) {
      if (boxesOverlap(labels[i].box, labels[j].box)) {
        violations.push({
          type: 'label-overlap',
          detail: `标签「${labels[i].text}」与「${labels[j].text}」重叠`,
        });
      }
    }
  }

  // 4) 管线中点标注压住设备标签 / 设备本体
  for (const el of edgeLabels) {
    for (const l of labels) {
      if (boxesOverlap(el.box, l.box, 2)) {
        violations.push({
          type: 'edgelabel-through',
          detail: `管线标注「${el.text}」（${el.edgeId}）压住标签「${l.text}」`,
        });
      }
    }
    for (const body of bodies) {
      const shrunk: Box = {
        left: body.box.left + 3,
        top: body.box.top + 3,
        right: body.box.right - 3,
        bottom: body.box.bottom - 3,
      };
      if (boxesOverlap(el.box, shrunk)) {
        violations.push({
          type: 'edgelabel-through',
          detail: `管线标注「${el.text}」（${el.edgeId}）压住设备「${body.text}」`,
        });
      }
    }
  }

  return violations;
}

/** 挂到 window.__pidLint，供浏览器控制台 / 自动化调用 */
export function installPidLint(): void {
  (window as unknown as { __pidLint: typeof collectPidViolations }).__pidLint =
    collectPidViolations;
}
